import { isApprovedNextImageRemoteSource } from "@/imageRemotePolicy.mjs";
import {
  resolveReviewFacetPublicationV1,
  type ReviewFacetPublicationV1,
  type ReviewTeamMemberFacetPublicationV1,
} from "@/app/components/reviews/reviewsPublication";
import {
  resolveReviewsOwnerScope,
  type ReviewsOwnerScope,
} from "@/app/components/reviews/reviewsCriteria";

export interface LocationServiceReviewSummaryRowV1 {
  serviceId: string;
  averageRating: number;
  reviewCount: number;
  /**
   * Optional complete rows for the shared Reviews modal. Aggregate-only
   * evidence remains useful for a static summary, but never creates an empty
   * or misleading dialog trigger.
   */
  reviews?: readonly LocationServiceReviewV1[];
  /**
   * Exact Team facet descendant for this service's complete review rows.
   * It is optional because a service origin never proves the professional.
   */
  teamMemberFacetRevision?: string;
  teamMemberFacetPublication?: ReviewTeamMemberFacetPublicationV1;
}

export interface LocationServiceReviewV1 {
  id: string;
  serviceId: string;
  reviewer: {
    displayName: string;
    avatarSrc: string | null;
  };
  rating: 1 | 2 | 3 | 4 | 5;
  publishedAt: string;
  body: string | null;
  verifiedBooking: boolean;
  reply: {
    authorName: string;
    body: string;
  } | null;
}

/**
 * Presentation-only service review evidence. The publication is owned by the
 * exact Location catalog, not by a technical catalog assignment or offering.
 */
export interface LocationServiceReviewSummaryPublicationV1 {
  schemaVersion: 1;
  providerId: string;
  providerRevision: string;
  locationId: string;
  catalogVersion: string;
  /** Exact revision of the complete review rows, required by facet evidence. */
  reviewsRevision?: string;
  /** Exact Team catalog joined by optional row-level facet descendants. */
  teamMemberCatalogRevision?: string;
  rows: readonly LocationServiceReviewSummaryRowV1[];
}

export interface ServiceReviewSummary {
  averageRating: number;
  count: number;
  /** Exact publication identity when the summary crossed a server boundary. */
  ownerKey?: string;
  /** Exact Reviews owner, independent from optional filter-facet descendants. */
  reviewsOwnerScope?: ReviewsOwnerScope;
  /** Complete exact rows when the provider publishes the modal payload. */
  reviews?: readonly LocationServiceReviewV1[];
  /**
   * Optional exact filter metadata for the shared Reviews owner. Service
   * summaries can publish Team only; the fixed service always omits Services.
   */
  reviewFacetPublication?: ReviewFacetPublicationV1;
}

export interface PublishedLocationServiceReviewSummary
  extends ServiceReviewSummary {
  serviceId: string;
  ownerKey: string;
}

export const EMPTY_LOCATION_SERVICE_REVIEW_SUMMARIES: readonly PublishedLocationServiceReviewSummary[] =
  Object.freeze([]);

/**
 * Current Location Team authority supplied independently from review data.
 * Review publications may reference only these exact public assignments and
 * must carry this exact catalog revision before Team filtering is exposed.
 */
export interface LocationServiceReviewTeamCatalogAuthorityV1 {
  readonly schemaVersion: 1;
  readonly locationId: string;
  readonly catalogVersion: string;
  readonly catalogRevision: string;
  readonly userLocationIds: readonly string[];
}

export interface ResolveLocationServiceReviewSummaryInput {
  locationId: string;
  catalogVersion: string;
  serviceId: string;
  teamMemberCatalogAuthority: LocationServiceReviewTeamCatalogAuthorityV1 | null;
  publications: readonly LocationServiceReviewSummaryPublicationV1[];
}

export interface ResolveLocationServiceReviewSummariesInput {
  locationId: string;
  catalogVersion: string;
  serviceIds: readonly string[];
  teamMemberCatalogAuthority: LocationServiceReviewTeamCatalogAuthorityV1 | null;
  publications: readonly LocationServiceReviewSummaryPublicationV1[];
}

function isCanonicalPublicationText(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 200
  );
}

function isRecord(value: unknown): value is Record<PropertyKey, unknown> {
  return typeof value === "object" && value !== null;
}

function readDenseArraySnapshot<T>(value: readonly T[]): readonly T[] | null;
function readDenseArraySnapshot(value: unknown): readonly unknown[] | null;
function readDenseArraySnapshot(value: unknown): readonly unknown[] | null {
  try {
    if (!Array.isArray(value)) {
      return null;
    }

    const length = value.length;
    const snapshot = new Array<unknown>(length);

    for (let index = 0; index < length; index += 1) {
      if (!Object.prototype.hasOwnProperty.call(value, index)) {
        return null;
      }

      snapshot[index] = value[index];
    }

    if (value.length !== length) {
      return null;
    }

    for (let index = 0; index < length; index += 1) {
      if (!Object.prototype.hasOwnProperty.call(value, index)) {
        return null;
      }
    }

    return Object.freeze(snapshot);
  } catch {
    return null;
  }
}

function isValidSummaryValue(value: unknown) {
  if (!isRecord(value)) {
    return false;
  }

  return (
    Number.isSafeInteger(value.reviewCount) &&
    Number(value.reviewCount) > 0 &&
    Number.isFinite(value.averageRating) &&
    Number(value.averageRating) >= 1 &&
    Number(value.averageRating) <= 5
  );
}

function isCanonicalReviewCopy(
  value: unknown,
  maximumLength = 5_000,
): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= maximumLength
  );
}

function isCanonicalIsoInstant(value: unknown) {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString() === value
  );
}

function isRenderableReviewerAvatarSource(value: unknown): value is string {
  if (!isCanonicalReviewCopy(value, 2_000)) {
    return false;
  }

  if (value.startsWith("/") && !value.startsWith("//")) {
    return true;
  }

  return isApprovedNextImageRemoteSource(value);
}

function isValidServiceReviewSnapshot(
  value: unknown,
  serviceId: string,
): value is LocationServiceReviewV1 {
  if (!isRecord(value)) {
    return false;
  }

  const reviewer = value.reviewer;
  const reply = value.reply;

  return (
    isCanonicalPublicationText(value.id) &&
    value.serviceId === serviceId &&
    isRecord(reviewer) &&
    isCanonicalReviewCopy(reviewer.displayName, 500) &&
    (reviewer.avatarSrc === null ||
      isRenderableReviewerAvatarSource(reviewer.avatarSrc)) &&
    Number.isSafeInteger(value.rating) &&
    Number(value.rating) >= 1 &&
    Number(value.rating) <= 5 &&
    isCanonicalIsoInstant(value.publishedAt) &&
    (value.body === null || isCanonicalReviewCopy(value.body)) &&
    typeof value.verifiedBooking === "boolean" &&
    (reply === null ||
      (isRecord(reply) &&
        isCanonicalReviewCopy(reply.authorName, 500) &&
        isCanonicalReviewCopy(reply.body)))
  );
}

function detachValidServiceReview(
  value: unknown,
  serviceId: string,
): LocationServiceReviewV1 | null {
  try {
    if (!isRecord(value)) {
      return null;
    }

    const id = value.id;
    const reviewServiceId = value.serviceId;
    const reviewerValue = value.reviewer;
    const rating = value.rating;
    const publishedAt = value.publishedAt;
    const body = value.body;
    const verifiedBooking = value.verifiedBooking;
    const replyValue = value.reply;

    if (!isRecord(reviewerValue)) {
      return null;
    }

    const reviewer = {
      displayName: reviewerValue.displayName,
      avatarSrc: reviewerValue.avatarSrc,
    };
    const reply =
      replyValue === null
        ? null
        : isRecord(replyValue)
          ? {
              authorName: replyValue.authorName,
              body: replyValue.body,
            }
          : undefined;
    const snapshot = {
      id,
      serviceId: reviewServiceId,
      reviewer,
      rating,
      publishedAt,
      body,
      verifiedBooking,
      reply,
    };

    if (!isValidServiceReviewSnapshot(snapshot, serviceId)) {
      return null;
    }

    return Object.freeze<LocationServiceReviewV1>({
      id: snapshot.id,
      serviceId: snapshot.serviceId,
      reviewer: Object.freeze({
        displayName: snapshot.reviewer.displayName,
        avatarSrc: snapshot.reviewer.avatarSrc,
      }),
      rating: snapshot.rating,
      publishedAt: snapshot.publishedAt,
      body: snapshot.body,
      verifiedBooking: snapshot.verifiedBooking,
      reply: snapshot.reply
        ? Object.freeze({
            authorName: snapshot.reply.authorName,
            body: snapshot.reply.body,
          })
        : null,
    });
  } catch {
    return null;
  }
}

function getValidCompleteServiceReviews(
  value: unknown,
  serviceId: string,
  summary: ServiceReviewSummary,
) {
  try {
    const sourceRows = readDenseArraySnapshot(value);

    if (!sourceRows || sourceRows.length !== summary.count) {
      return null;
    }

    const reviews = sourceRows.map((review) =>
      detachValidServiceReview(review, serviceId),
    );

    if (reviews.some((review) => review === null)) {
      return null;
    }

    const detachedReviews = reviews as LocationServiceReviewV1[];
    const ids = detachedReviews.map((review) => review.id);

    if (new Set(ids).size !== ids.length) {
      return null;
    }

    const average =
      detachedReviews.reduce((total, review) => total + review.rating, 0) /
      detachedReviews.length;

    if (Math.round(average * 10) / 10 !== summary.averageRating) {
      return null;
    }

    return Object.freeze(detachedReviews);
  } catch {
    return null;
  }
}

interface DetachedReviewPublicationRow {
  readonly serviceId: unknown;
  readonly averageRating: unknown;
  readonly reviewCount: unknown;
  readonly reviews: unknown;
  readonly teamMemberFacetRevision: unknown;
  readonly teamMemberFacetPublication: unknown;
}

interface DetachedReviewPublication {
  readonly providerId: string;
  readonly providerRevision: string;
  readonly reviewsRevision: unknown;
  readonly teamMemberCatalogRevision: unknown;
  readonly rows: readonly DetachedReviewPublicationRow[];
}

interface DetachedTeamMemberCatalogAuthority {
  readonly catalogRevision: string;
  readonly userLocationIds: readonly string[];
}

interface DetachedProjectedServiceReviewSummary {
  readonly serviceId: unknown;
  readonly averageRating: unknown;
  readonly count: unknown;
  readonly ownerKey: unknown;
  readonly reviewsOwnerScope: ReviewsOwnerScope | null;
  readonly reviews: unknown;
  readonly reviewFacetPublication: unknown;
}

function detachTeamMemberFacetPublication(
  value: unknown,
): Record<PropertyKey, unknown> | undefined {
  try {
    if (!isRecord(value)) {
      return undefined;
    }

    const catalogRevision = value.catalogRevision;
    const facetRevision = value.facetRevision;
    const optionValues = readDenseArraySnapshot(value.options);
    const bindingValues = readDenseArraySnapshot(value.bindings);

    if (!optionValues || !bindingValues) {
      return undefined;
    }

    const options: Record<PropertyKey, unknown>[] = [];
    for (const optionValue of optionValues) {
      if (!isRecord(optionValue)) {
        return undefined;
      }

      options.push(
        Object.freeze({
          userLocationId: optionValue.userLocationId,
          displayName: optionValue.displayName,
          avatarSrc: optionValue.avatarSrc,
          averageRating: optionValue.averageRating,
          reviewCount: optionValue.reviewCount,
        }),
      );
    }

    const bindings: Record<PropertyKey, unknown>[] = [];
    for (const bindingValue of bindingValues) {
      if (!isRecord(bindingValue)) {
        return undefined;
      }

      const userLocationIds = readDenseArraySnapshot(
        bindingValue.userLocationIds,
      );

      if (!userLocationIds) {
        return undefined;
      }

      bindings.push(
        Object.freeze({
          reviewId: bindingValue.reviewId,
          userLocationIds,
        }),
      );
    }

    return Object.freeze({
      catalogRevision,
      facetRevision,
      options: Object.freeze(options),
      bindings: Object.freeze(bindings),
    });
  } catch {
    return undefined;
  }
}

function detachReviewPublicationRow(
  value: unknown,
): DetachedReviewPublicationRow | null {
  try {
    if (!isRecord(value)) {
      return null;
    }

    const serviceId = value.serviceId;
    const averageRating = value.averageRating;
    const reviewCount = value.reviewCount;
    let teamMemberFacetRevision: unknown;
    let teamMemberFacetValue: unknown;
    let reviews: unknown;

    try {
      teamMemberFacetRevision = value.teamMemberFacetRevision;
    } catch {
      teamMemberFacetRevision = undefined;
    }

    try {
      teamMemberFacetValue = value.teamMemberFacetPublication;
    } catch {
      teamMemberFacetValue = undefined;
    }

    const teamMemberFacetPublication = detachTeamMemberFacetPublication(
      teamMemberFacetValue,
    );

    try {
      reviews = value.reviews;
    } catch {
      reviews = undefined;
    }

    return Object.freeze({
      serviceId,
      averageRating,
      reviewCount,
      reviews,
      teamMemberFacetRevision,
      teamMemberFacetPublication,
    });
  } catch {
    return null;
  }
}

function detachTeamMemberCatalogAuthority(
  value: unknown,
  input: { readonly locationId: string; readonly catalogVersion: string },
): DetachedTeamMemberCatalogAuthority | null {
  try {
    if (value === null || !isRecord(value)) {
      return null;
    }

    const schemaVersion = value.schemaVersion;
    const locationId = value.locationId;
    const catalogVersion = value.catalogVersion;
    const catalogRevision = value.catalogRevision;
    const userLocationIdValues = readDenseArraySnapshot(
      value.userLocationIds,
    );

    if (
      schemaVersion !== 1 ||
      locationId !== input.locationId ||
      catalogVersion !== input.catalogVersion ||
      !isCanonicalPublicationText(catalogRevision) ||
      !userLocationIdValues ||
      userLocationIdValues.length === 0 ||
      userLocationIdValues.some(
        (userLocationId) => !isCanonicalPublicationText(userLocationId),
      ) ||
      new Set(userLocationIdValues).size !== userLocationIdValues.length
    ) {
      return null;
    }

    return Object.freeze({
      catalogRevision,
      userLocationIds: Object.freeze(
        userLocationIdValues.map((userLocationId) =>
          String(userLocationId),
        ),
      ),
    });
  } catch {
    return null;
  }
}

function detachExactReviewPublication(
  value: unknown,
  locationId: string,
  catalogVersion: string,
): DetachedReviewPublication | null {
  const publications = readDenseArraySnapshot(value);

  if (!publications) {
    return null;
  }

  try {
    const candidates: Array<{
      publication: Record<PropertyKey, unknown>;
      locationId: unknown;
      catalogVersion: unknown;
    }> = [];

    for (const publication of publications) {
      if (!isRecord(publication)) {
        continue;
      }

      const publicationLocationId = publication.locationId;
      const publicationCatalogVersion = publication.catalogVersion;

      if (
        publicationLocationId === locationId &&
        publicationCatalogVersion === catalogVersion
      ) {
        candidates.push({
          publication,
          locationId: publicationLocationId,
          catalogVersion: publicationCatalogVersion,
        });
      }
    }

    if (candidates.length !== 1) {
      return null;
    }

    const candidate = candidates[0]!;
    const schemaVersion = candidate.publication.schemaVersion;
    const providerId = candidate.publication.providerId;
    const providerRevision = candidate.publication.providerRevision;
    let reviewsRevision: unknown;
    let teamMemberCatalogRevision: unknown;

    try {
      reviewsRevision = candidate.publication.reviewsRevision;
    } catch {
      reviewsRevision = undefined;
    }

    try {
      teamMemberCatalogRevision =
        candidate.publication.teamMemberCatalogRevision;
    } catch {
      teamMemberCatalogRevision = undefined;
    }

    const rowValues = readDenseArraySnapshot(candidate.publication.rows);

    if (
      schemaVersion !== 1 ||
      !isCanonicalPublicationText(providerId) ||
      !isCanonicalPublicationText(providerRevision) ||
      !rowValues
    ) {
      return null;
    }

    const rows: DetachedReviewPublicationRow[] = [];
    for (const row of rowValues) {
      const detachedRow = detachReviewPublicationRow(row);

      if (!detachedRow) {
        return null;
      }

      rows.push(detachedRow);
    }

    return Object.freeze({
      providerId,
      providerRevision,
      reviewsRevision,
      teamMemberCatalogRevision,
      rows: Object.freeze(rows),
    });
  } catch {
    return null;
  }
}

function hasReferencedTeamMemberOptions(
  publication: ReviewFacetPublicationV1,
  reviewCount: number,
) {
  const teamMembers = publication.teamMembers;

  if (
    !teamMembers ||
    reviewCount <= 0 ||
    teamMembers.bindings.length !== reviewCount
  ) {
    return false;
  }

  const referencedUserLocationIds = new Set(
    teamMembers.bindings.flatMap((binding) => binding.userLocationIds),
  );

  if (
    referencedUserLocationIds.size !== teamMembers.options.length ||
    teamMembers.options.some(
      (option) =>
        !referencedUserLocationIds.has(option.userLocationId),
    )
  ) {
    return false;
  }

  return teamMembers.options.every((option) =>
    referencedUserLocationIds.has(option.userLocationId),
  );
}

function resolveExactServiceReviewFacetPublication(input: {
  readonly owner: ReviewsOwnerScope;
  readonly reviewIds: readonly string[];
  readonly publishedTeamMemberCatalogRevision: unknown;
  readonly teamMemberCatalogAuthority: DetachedTeamMemberCatalogAuthority;
  readonly teamMemberFacetRevision: unknown;
  readonly teamMemberFacetPublication: unknown;
}): ReviewFacetPublicationV1 | null {
  const {
    owner,
    reviewIds,
    publishedTeamMemberCatalogRevision,
    teamMemberCatalogAuthority,
    teamMemberFacetRevision,
    teamMemberFacetPublication,
  } = input;

  if (
    publishedTeamMemberCatalogRevision !==
      teamMemberCatalogAuthority.catalogRevision ||
    !isCanonicalPublicationText(teamMemberFacetRevision)
  ) {
    return null;
  }

  const publication = resolveReviewFacetPublicationV1(
    {
      schemaVersion: 1,
      owner,
      services: null,
      teamMembers: teamMemberFacetPublication,
    },
    {
      owner,
      reviewIds,
      services: null,
      teamMembers: {
        catalogRevision: teamMemberCatalogAuthority.catalogRevision,
        facetRevision: teamMemberFacetRevision,
      },
    },
  );

  if (
    !publication ||
    publication.services !== null ||
    !hasReferencedTeamMemberOptions(publication, reviewIds.length) ||
    publication.teamMembers!.options.some(
      (option) =>
        !teamMemberCatalogAuthority.userLocationIds.includes(
          option.userLocationId,
        ),
    )
  ) {
    return null;
  }

  return publication;
}

function parseServiceReviewOwnerKey(
  ownerKey: string,
  serviceId: string,
): { readonly providerId: string; readonly providerRevision: string } | null {
  try {
    const parts = readDenseArraySnapshot(JSON.parse(ownerKey));

    if (
      !parts ||
      parts.length !== 5 ||
      parts.some((part) => !isCanonicalPublicationText(part)) ||
      parts[4] !== serviceId ||
      JSON.stringify(parts) !== ownerKey
    ) {
      return null;
    }

    return Object.freeze({
      providerId: parts[0] as string,
      providerRevision: parts[1] as string,
    });
  } catch {
    return null;
  }
}

function resolveProjectedServiceReviewFacetPublication(
  value: unknown,
  input: {
    readonly owner: ReviewsOwnerScope;
    readonly serviceId: string;
    readonly reviewIds: readonly string[];
  },
): ReviewFacetPublicationV1 | null {
  try {
    if (!isRecord(value)) {
      return null;
    }

    const schemaVersion = value.schemaVersion;
    const owner = resolveReviewsOwnerScope(value.owner);
    const services = value.services;
    const teamMemberValue = value.teamMembers;
    const parsedOwner = parseServiceReviewOwnerKey(
      input.owner.ownerKey,
      input.serviceId,
    );

    if (
      schemaVersion !== 1 ||
      !owner ||
      !parsedOwner ||
      services !== null ||
      owner.ownerKey !== input.owner.ownerKey ||
      owner.providerId !== input.owner.providerId ||
      owner.providerRevision !== input.owner.providerRevision ||
      owner.reviewsRevision !== input.owner.reviewsRevision ||
      owner.providerId !== parsedOwner.providerId ||
      owner.providerRevision !== parsedOwner.providerRevision ||
      !isRecord(teamMemberValue)
    ) {
      return null;
    }

    const teamMemberFacetPublication =
      detachTeamMemberFacetPublication(teamMemberValue);

    if (!teamMemberFacetPublication) {
      return null;
    }

    const catalogRevision = teamMemberFacetPublication.catalogRevision;
    const facetRevision = teamMemberFacetPublication.facetRevision;

    if (
      !isCanonicalPublicationText(catalogRevision) ||
      !isCanonicalPublicationText(facetRevision)
    ) {
      return null;
    }

    const publication = resolveReviewFacetPublicationV1(
      {
        schemaVersion: 1,
        owner: input.owner,
        services: null,
        teamMembers: teamMemberFacetPublication,
      },
      {
        owner: input.owner,
        reviewIds: input.reviewIds,
        services: null,
        teamMembers: {
          catalogRevision,
          facetRevision,
        },
      },
    );

    return publication &&
      hasReferencedTeamMemberOptions(publication, input.reviewIds.length)
      ? publication
      : null;
  } catch {
    return null;
  }
}

function detachProjectedServiceReviewFacetPublication(
  value: unknown,
): Record<PropertyKey, unknown> | undefined {
  try {
    if (!isRecord(value)) {
      return undefined;
    }

    const schemaVersion = value.schemaVersion;
    const owner = resolveReviewsOwnerScope(value.owner);
    const services = value.services;
    const teamMembers = detachTeamMemberFacetPublication(value.teamMembers);

    if (!owner || !teamMembers) {
      return undefined;
    }

    return Object.freeze({
      schemaVersion,
      owner,
      services,
      teamMembers,
    });
  } catch {
    return undefined;
  }
}

function detachProjectedServiceReviewSummary(
  value: Record<PropertyKey, unknown>,
  serviceId: unknown,
): DetachedProjectedServiceReviewSummary | null {
  try {
    const averageRating = value.averageRating;
    const count = value.count;
    const ownerKey = value.ownerKey;
    let reviewsOwnerScope: ReviewsOwnerScope | null = null;
    let reviewFacetPublication: unknown;
    let reviews: unknown;

    try {
      reviewsOwnerScope = resolveReviewsOwnerScope(value.reviewsOwnerScope);
    } catch {
      reviewsOwnerScope = null;
    }

    try {
      reviewFacetPublication =
        detachProjectedServiceReviewFacetPublication(
          value.reviewFacetPublication,
        );
    } catch {
      reviewFacetPublication = undefined;
    }

    try {
      reviews = value.reviews;
    } catch {
      reviews = undefined;
    }

    return Object.freeze({
      serviceId,
      averageRating,
      count,
      ownerKey,
      reviewsOwnerScope,
      reviews,
      reviewFacetPublication,
    });
  } catch {
    return null;
  }
}

function resolveDetachedServiceReviewSummary(
  publication: DetachedReviewPublication,
  input: {
    readonly locationId: string;
    readonly catalogVersion: string;
    readonly serviceId: string;
    readonly teamMemberCatalogAuthority: DetachedTeamMemberCatalogAuthority | null;
  },
): (ServiceReviewSummary & { ownerKey: string }) | null {
  try {
    const candidates = publication.rows.filter(
      (row) => row.serviceId === input.serviceId,
    );

    if (candidates.length !== 1) {
      return null;
    }

    const candidate = candidates[0]!;
    const averageRating = candidate.averageRating;
    const reviewCount = candidate.reviewCount;

    if (
      !isCanonicalPublicationText(candidate.serviceId) ||
      !isValidSummaryValue({ averageRating, reviewCount })
    ) {
      return null;
    }

    const summary = {
      averageRating: Number(averageRating),
      count: Number(reviewCount),
      ownerKey: JSON.stringify([
        publication.providerId,
        publication.providerRevision,
        input.locationId,
        input.catalogVersion,
        input.serviceId,
      ]),
    };
    const reviews = getValidCompleteServiceReviews(
      candidate.reviews,
      input.serviceId,
      summary,
    );

    if (!reviews) {
      return summary;
    }

    const reviewsOwnerScope = resolveReviewsOwnerScope({
      ownerKey: summary.ownerKey,
      providerId: publication.providerId,
      providerRevision: publication.providerRevision,
      reviewsRevision: publication.reviewsRevision,
    });

    if (!reviewsOwnerScope) {
      return summary;
    }

    const reviewFacetPublication = input.teamMemberCatalogAuthority
      ? resolveExactServiceReviewFacetPublication({
          owner: reviewsOwnerScope,
          reviewIds: reviews.map((review) => review.id),
          publishedTeamMemberCatalogRevision:
            publication.teamMemberCatalogRevision,
          teamMemberCatalogAuthority: input.teamMemberCatalogAuthority,
          teamMemberFacetRevision: candidate.teamMemberFacetRevision,
          teamMemberFacetPublication: candidate.teamMemberFacetPublication,
        })
      : null;

    return reviewFacetPublication
      ? {
          ...summary,
          reviews,
          reviewsOwnerScope,
          reviewFacetPublication,
        }
      : { ...summary, reviews, reviewsOwnerScope };
  } catch {
    return null;
  }
}

/**
 * Resolves exactly one service-specific public aggregate. Location and
 * Specialist totals are never fallbacks. Missing, duplicate, malformed, or
 * stale evidence withdraws only this review row, never the booking catalog.
 */
export function resolveLocationServiceReviewSummary(
  input: ResolveLocationServiceReviewSummaryInput,
): (ServiceReviewSummary & { ownerKey: string }) | null {
  try {
    const {
      locationId,
      catalogVersion,
      serviceId,
      teamMemberCatalogAuthority,
      publications,
    } = input;

    if (
      !isCanonicalPublicationText(locationId) ||
      !isCanonicalPublicationText(catalogVersion) ||
      !isCanonicalPublicationText(serviceId)
    ) {
      return null;
    }

    const publication = detachExactReviewPublication(
      publications,
      locationId,
      catalogVersion,
    );
    const detachedTeamMemberCatalogAuthority =
      detachTeamMemberCatalogAuthority(teamMemberCatalogAuthority, {
        locationId,
        catalogVersion,
      });

    return publication
      ? resolveDetachedServiceReviewSummary(publication, {
          locationId,
          catalogVersion,
          serviceId,
          teamMemberCatalogAuthority: detachedTeamMemberCatalogAuthority,
        })
      : null;
  } catch {
    return null;
  }
}

/** Resolves the serializable, presentation-only summaries for one catalog. */
export function resolveLocationServiceReviewSummaries(
  input: ResolveLocationServiceReviewSummariesInput,
): readonly PublishedLocationServiceReviewSummary[] {
  try {
    const {
      locationId,
      catalogVersion,
      serviceIds,
      teamMemberCatalogAuthority,
      publications,
    } = input;
    const serviceIdSnapshot = readDenseArraySnapshot(serviceIds);

    if (
      !isCanonicalPublicationText(locationId) ||
      !isCanonicalPublicationText(catalogVersion) ||
      !serviceIdSnapshot
    ) {
      return Object.freeze([]);
    }

    const uniqueServiceIds = [...new Set(serviceIdSnapshot)];

    if (uniqueServiceIds.length !== serviceIdSnapshot.length) {
      return Object.freeze([]);
    }

    const publication = detachExactReviewPublication(
      publications,
      locationId,
      catalogVersion,
    );

    if (!publication) {
      return Object.freeze([]);
    }

    const detachedTeamMemberCatalogAuthority =
      detachTeamMemberCatalogAuthority(teamMemberCatalogAuthority, {
        locationId,
        catalogVersion,
      });

    return Object.freeze(
      uniqueServiceIds.flatMap((serviceId) => {
        if (!isCanonicalPublicationText(serviceId)) {
          return [];
        }

        const summary = resolveDetachedServiceReviewSummary(publication, {
          locationId,
          catalogVersion,
          serviceId,
          teamMemberCatalogAuthority: detachedTeamMemberCatalogAuthority,
        });

        return summary
          ? [
              Object.freeze({
                serviceId,
                ...summary,
              }),
            ]
          : [];
      }),
    );
  } catch {
    return Object.freeze([]);
  }
}

/** Performs a strict service join at a reusable modal boundary. */
export function getPublishedLocationServiceReviewSummary(
  summaries: readonly PublishedLocationServiceReviewSummary[],
  serviceId: string,
): ServiceReviewSummary | null {
  try {
    const summarySnapshot = readDenseArraySnapshot(summaries);

    if (!summarySnapshot || !isCanonicalPublicationText(serviceId)) {
      return null;
    }

    const candidates: Array<{
      readonly source: Record<PropertyKey, unknown>;
      readonly serviceId: unknown;
    }> = [];
    for (const summary of summarySnapshot) {
      if (!isRecord(summary)) {
        continue;
      }

      const candidateServiceId = summary.serviceId;
      if (candidateServiceId === serviceId) {
        candidates.push({ source: summary, serviceId: candidateServiceId });
      }
    }

    const candidate =
      candidates.length === 1
        ? detachProjectedServiceReviewSummary(
            candidates[0]!.source,
            candidates[0]!.serviceId,
          )
        : null;
    const averageRating = candidate?.averageRating;
    const count = candidate?.count;
    const ownerKey = candidate?.ownerKey;

    if (
      !candidate ||
      candidate.serviceId !== serviceId ||
      !isValidSummaryValue({
        averageRating,
        reviewCount: count,
      }) ||
      !isCanonicalReviewCopy(ownerKey, 2_000)
    ) {
      return null;
    }

    const summary = {
      averageRating: Number(averageRating),
      count: Number(count),
      ownerKey,
    };
    const reviews = getValidCompleteServiceReviews(
      candidate.reviews,
      serviceId,
      summary,
    );

    const reviewsOwnerScope = candidate.reviewsOwnerScope;

    if (
      !reviews ||
      !reviewsOwnerScope ||
      reviewsOwnerScope.ownerKey !== ownerKey
    ) {
      return summary;
    }

    const reviewFacetPublication =
      resolveProjectedServiceReviewFacetPublication(
        candidate.reviewFacetPublication,
        {
          owner: reviewsOwnerScope,
          serviceId,
          reviewIds: reviews.map((review) => review.id),
        },
      );

    return reviewFacetPublication
      ? {
          ...summary,
          reviews,
          reviewsOwnerScope,
          reviewFacetPublication,
        }
      : { ...summary, reviews, reviewsOwnerScope };
  } catch {
    return null;
  }
}

/**
 * Narrows an aggregate into the complete exact row set required by the shared
 * Reviews modal. Callers must keep the summary static when this returns null.
 */
export function getCompleteServiceReviewRows(
  summary: ServiceReviewSummary | null | undefined,
  serviceId: string,
): readonly LocationServiceReviewV1[] | null {
  try {
    if (
      !summary ||
      !isCanonicalPublicationText(serviceId) ||
      !isRecord(summary)
    ) {
      return null;
    }

    const averageRating = summary.averageRating;
    const count = summary.count;
    const reviews = summary.reviews;

    if (!isValidSummaryValue({ averageRating, reviewCount: count })) {
      return null;
    }

    return getValidCompleteServiceReviews(
      reviews,
      serviceId,
      { averageRating: Number(averageRating), count: Number(count) },
    );
  } catch {
    return null;
  }
}

export function formatServiceReviewRating(locale: string, rating: number) {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  }).format(rating);
}
